import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import {
  copyCategory,
  createCategory,
  deleteCategory,
  listCategoriesRecords,
  syncCategoriesFromApi,
  updateCategory,
  type CategoryRecord,
} from "@/lib/catalog/categoriesStore";
import { listCatalogAttributeRecords } from "@/lib/catalog/catalogAttributesStore";
import {
  listLinksForCategory,
  removeLinksForCategory,
  setCategoryAttributeLinksViaApi,
  syncLinksForCategoryFromApi,
} from "@/lib/catalog/categoryAttributeLinksStore";
import { listProducts } from "@/lib/catalog/productsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import type { ID } from "@/lib/catalog/types";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowDown, ArrowUp, CalendarIcon, Check, Copy, Edit, Eye, Filter, LayoutGrid, List, ListTree, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { useGridPagination } from "@/hooks/useGridPagination";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/sonner";

const emptyForm = { name: "", orderedAttributeIds: [] as ID[] };
const CATEGORIES_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatCategoryDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type CatSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "fields_asc"
  | "fields_desc";

const Categories = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "category_can_view");
  const canCreate = can(user, "category_can_create");
  const canUpdate = can(user, "category_can_update");
  const canDelete = can(user, "category_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<CatSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [minFields, setMinFields] = useState("");
  const [onlyWithFields, setOnlyWithFields] = useState(false);
  const [onlyWithoutFields, setOnlyWithoutFields] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [attrPickerOpen, setAttrPickerOpen] = useState(false);
  const [attrSearch, setAttrSearch] = useState("");
  const [editing, setEditing] = useState<CategoryRecord | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<CategoryRecord | null>(null);
  const [viewCategory, setViewCategory] = useState<CategoryRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copyBusyId, setCopyBusyId] = useState<ID | null>(null);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const categories = useMemo(() => listCategoriesRecords(), [version]);
  const attributes = useMemo(() => listCatalogAttributeRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const fieldCountByCategoryId = useMemo(() => {
    const map = new Map<ID, number>();
    for (const c of categories) {
      map.set(c.id, listLinksForCategory(c.id).length);
    }
    return map;
  }, [categories, version]);

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsForbidden(false);
      try {
        await syncCategoriesFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Categories] Load failed", error);
        if (mounted) toast.error("Could not fetch categories. Showing cached data.");
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const minFieldsNum = Number(minFields);
    const hasMinFields = Number.isFinite(minFieldsNum) && minFields.trim() !== "";

    return categories.filter((c) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(c.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(c.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (q) {
        const ok =
          c.name.toLowerCase().includes(q) ||
          String(fieldCountByCategoryId.get(c.id) ?? "").includes(q);
        if (!ok) return false;
      }
      if (nameNeedle && !c.name.toLowerCase().includes(nameNeedle)) return false;

      const count = fieldCountByCategoryId.get(c.id) ?? 0;
      if (onlyWithFields && count <= 0) return false;
      if (onlyWithoutFields && count > 0) return false;
      if (hasMinFields && count < minFieldsNum) return false;

      return true;
    });
  }, [
    categories,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
    onlyWithFields,
    onlyWithoutFields,
    minFields,
    fieldCountByCategoryId,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const fa = fieldCountByCategoryId.get(a.id) ?? 0;
      const fb = fieldCountByCategoryId.get(b.id) ?? 0;
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "fields_desc":
          return fb - fa;
        case "fields_asc":
          return fa - fb;
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy, fieldCountByCategoryId]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (minFields.trim() ? 1 : 0) +
    (onlyWithFields ? 1 : 0) +
    (onlyWithoutFields ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setMinFields("");
    setOnlyWithFields(false);
    setOnlyWithoutFields(false);
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${minFields}|${onlyWithFields ? "1" : "0"}|${onlyWithoutFields ? "1" : "0"}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: CATEGORIES_PAGE_SIZE_OPTIONS,
    defaultPageSize: CATEGORIES_PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setOpen(true);
  };

  const openEdit = (c: CategoryRecord) => {
    setEditing(c);
    const openWithLinks = async () => {
      try {
        await syncLinksForCategoryFromApi(c.id);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          toast.error("You don’t have permission to load category fields (category_can_view).");
          return;
        }
        console.error("[Categories] Load links failed", error);
      }
      const ordered = listLinksForCategory(c.id).map((l) => l.attributeId);
      setForm({ name: c.name, orderedAttributeIds: ordered });
      setOpen(true);
    };
    void openWithLinks();
  };

  const attrLabel = (id: ID) => {
    const a = attributes.find((x) => x.id === id);
    return a ? `${a.name} (${a.code})` : id;
  };

  const selectedAttrIdSet = useMemo(() => new Set(form.orderedAttributeIds), [form.orderedAttributeIds]);

  const filteredAttributes = useMemo(() => {
    const q = attrSearch.trim().toLowerCase();
    if (!q) return attributes;
    return attributes.filter(
      (a) => a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q),
    );
  }, [attributes, attrSearch]);

  const filteredAttrSelectState = useMemo(() => {
    if (filteredAttributes.length === 0) return false as boolean | "indeterminate";
    const selectedCount = filteredAttributes.filter((a) => selectedAttrIdSet.has(a.id)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === filteredAttributes.length) return true;
    return "indeterminate";
  }, [filteredAttributes, selectedAttrIdSet]);

  const toggleAttribute = useCallback((id: ID) => {
    setForm((f) => ({
      ...f,
      orderedAttributeIds: f.orderedAttributeIds.includes(id)
        ? f.orderedAttributeIds.filter((x) => x !== id)
        : [...f.orderedAttributeIds, id],
    }));
  }, []);

  const toggleSelectAllFilteredAttributes = useCallback(
    (checked: boolean | "indeterminate") => {
      const filteredIds = filteredAttributes.map((a) => a.id);
      if (filteredIds.length === 0) return;
      setForm((f) => {
        if (checked === true) {
          const existing = new Set(f.orderedAttributeIds);
          const toAdd = filteredIds.filter((id) => !existing.has(id));
          return { ...f, orderedAttributeIds: [...f.orderedAttributeIds, ...toAdd] };
        }
        const remove = new Set(filteredIds);
        return {
          ...f,
          orderedAttributeIds: f.orderedAttributeIds.filter((id) => !remove.has(id)),
        };
      });
    },
    [filteredAttributes],
  );

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...form.orderedAttributeIds];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setForm((f) => ({ ...f, orderedAttributeIds: next }));
  };

  const save = async () => {
    if (isSaving) return;
    if (!form.name.trim()) {
      toast.error("Category name is required.");
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        const updateResult = await updateCategory(editing.id, {
          name: form.name,
          parentId: null,
        });
        await setCategoryAttributeLinksViaApi(editing.id, form.orderedAttributeIds);
        toast.success(updateResult?.message || "Category updated successfully.");
      } else {
        const created = await createCategory({
          name: form.name,
          parentId: null,
        });
        await setCategoryAttributeLinksViaApi(created.record.id, form.orderedAttributeIds);
        toast.success(created.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[Categories] Save failed", error);
      toast.error("Unable to save category. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (c: CategoryRecord) => {
    const n = listProducts().filter((p) => p.categoryId === c.id).length;
    if (n > 0) {
      setInfoDialog({
        title: "Cannot delete category",
        description: `${n} product(s) are still assigned to “${c.name}”. Move or delete those products first.`,
      });
      return;
    }
    setDeleteTarget(c);
  };

  const confirmRemoveCategory = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      removeLinksForCategory(deleteTarget.id);
      const result = await deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Categories] Delete failed", error);
      toast.error("Unable to delete category. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyCategory = useCallback(
    async (c: CategoryRecord) => {
      if (!canCreate || copyBusyId) return;
      setCopyBusyId(c.id);
      try {
        const result = await copyCategory(c.id);
        try {
          await syncLinksForCategoryFromApi(result.record.id);
        } catch (linkError) {
          console.error("[Categories] Sync copied links failed", linkError);
        }
        refresh();
        toast.success(result.message);
      } catch (error) {
        console.error("[Categories] Copy failed", error);
        if (isAxiosError(error) && error.response?.status === 403) {
          toast.error("You don’t have permission to copy categories (category_can_create).");
        } else {
          toast.error("Unable to copy category. Please try again.");
        }
      } finally {
        setCopyBusyId(null);
      }
    },
    [canCreate, copyBusyId, refresh],
  );

  const fieldCount = (categoryId: ID) => fieldCountByCategoryId.get(categoryId) ?? 0;

  return isForbidden ? (
    <PermissionDenied
      title="Categories access required"
      message="You don’t have permission to view categories. Ask an administrator to grant category_can_view_any."
    />
  ) : (
    <Layout title="Categories">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Categories</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage product categories and their attribute sets</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Category
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter categories</SheetTitle>
                  <SheetDescription>Refine by name, fields, flags, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Category</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Cars"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Minimum fields</Label>
                    <Input
                      className="glass-input h-10"
                      value={minFields}
                      onChange={(e) => setMinFields(e.target.value)}
                      placeholder="e.g. 3"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">With fields only</Label>
                      <p className="text-xs text-muted-foreground">At least 1 field</p>
                    </div>
                    <Checkbox
                      checked={onlyWithFields}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyWithFields(next);
                        if (next) setOnlyWithoutFields(false);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Without fields only</Label>
                      <p className="text-xs text-muted-foreground">0 fields</p>
                    </div>
                    <Checkbox
                      checked={onlyWithoutFields}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyWithoutFields(next);
                        if (next) setOnlyWithFields(false);
                      }}
                    />
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="categories-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={CATEGORIES_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as CatSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="fields_desc">Most fields</SelectItem>
                  <SelectItem value="fields_asc">Fewest fields</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="categories-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={CATEGORIES_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as CatSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="fields_desc">Most fields</SelectItem>
                    <SelectItem value="fields_asc">Fewest fields</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2 overflow-hidden flex flex-col min-h-0">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name *</Label>
                  <Input
                    className="glass-input h-10"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Cars"
                  />
                </div>
              </div>
              <div className="space-y-2 flex-1 min-h-0 flex flex-col">
                <Label className="text-xs text-muted-foreground">Fields for this category (order)</Label>
                <Popover
                  modal
                  open={attrPickerOpen}
                  onOpenChange={(next) => {
                    setAttrPickerOpen(next);
                    if (next) setAttrSearch("");
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="glass-input h-10 w-full justify-between font-normal"
                    >
                      <span className={cn("truncate", form.orderedAttributeIds.length === 0 && "text-muted-foreground")}>
                        {form.orderedAttributeIds.length > 0
                          ? `${form.orderedAttributeIds.length} attribute${form.orderedAttributeIds.length > 1 ? "s" : ""} selected`
                          : "Search and select attributes…"}
                      </span>
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[min(100vw-2rem,28rem)] p-0 z-[100]"
                    align="start"
                    sideOffset={6}
                  >
                    <div className="border-b border-border/50 p-2 space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          autoFocus
                          className="h-9 pl-8"
                          placeholder="Search attributes by name or code…"
                          value={attrSearch}
                          onChange={(e) => setAttrSearch(e.target.value)}
                        />
                      </div>
                      {filteredAttributes.length > 0 ? (
                        <label className="flex items-center gap-2 px-0.5 text-xs font-medium cursor-pointer">
                          <Checkbox
                            checked={filteredAttrSelectState}
                            onCheckedChange={(v) => toggleSelectAllFilteredAttributes(v === true)}
                            aria-label={filteredAttrSelectState === true ? "Deselect all attributes" : "Select all attributes"}
                          />
                          <span className="text-muted-foreground">
                            {filteredAttrSelectState === true ? "Deselect all" : "Select all"}
                            {attrSearch.trim() ? " (filtered)" : ""}
                          </span>
                        </label>
                      ) : null}
                    </div>
                    <div className="max-h-[240px] overflow-y-auto p-1">
                      {filteredAttributes.length === 0 ? (
                        <p className="px-2 py-6 text-center text-sm text-muted-foreground">No attributes found.</p>
                      ) : (
                        filteredAttributes.map((a) => {
                          const selected = selectedAttrIdSet.has(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => toggleAttribute(a.id)}
                              className={cn(
                                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60",
                                selected && "bg-muted/40",
                              )}
                            >
                              <span
                                className={cn(
                                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                  selected ? "border-primary/50 bg-primary/10" : "border-border",
                                )}
                              >
                                {selected ? <Check className="h-3 w-3 text-primary" /> : null}
                              </span>
                              <span className="flex-1 truncate">
                                {a.name} <span className="text-muted-foreground">({a.code})</span>
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <ScrollArea className="h-[200px] rounded-lg border border-border/50 bg-muted/20">
                  <ul className="p-2 space-y-1">
                    {form.orderedAttributeIds.length === 0 ? (
                      <li className="px-2 py-6 text-center text-xs text-muted-foreground">
                        No attributes selected yet.
                      </li>
                    ) : null}
                    {form.orderedAttributeIds.map((id, idx) => (
                      <li
                        key={`${id}-${idx}`}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 bg-card border border-border/40 text-sm"
                      >
                        <span className="flex-1 truncate">{attrLabel(id)}</span>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, -1)}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, 1)}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              orderedAttributeIds: f.orderedAttributeIds.filter((_, i) => i !== idx),
                            }))
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={isSaving}>Cancel</Button>
              <Button variant="premium" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewCategory} onOpenChange={(o) => !o && setViewCategory(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Category details</DialogTitle>
            </DialogHeader>
            {viewCategory ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Name</dt>
                  <dd className="font-medium text-foreground">{viewCategory.name}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Fields</dt>
                  <dd className="text-muted-foreground">{fieldCount(viewCategory.id)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatCategoryDate(viewCategory.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Last updated</dt>
                  <dd className="text-muted-foreground">{formatCategoryDate(viewCategory.updatedAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewCategory(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {categories.length === 0
              ? canCreate
                ? "No categories yet. Click “Add Category” to create one."
                : "No categories yet."
              : "No categories match your search."}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={c.name}
                  primaryMeta={{
                    label: "Fields",
                    value: String(fieldCount(c.id)),
                    icon: ListTree,
                    iconClassName: "text-red-600",
                  }}
                  footer={`Last updated: ${formatCatalogAuditDate(c.updatedAt)}`}
                  onView={canView ? () => setViewCategory(c) : undefined}
                  onEdit={canUpdate ? () => openEdit(c) : undefined}
                  onCopy={canCreate ? () => void handleCopyCategory(c) : undefined}
                  copyLoading={copyBusyId === c.id}
                  onDelete={canDelete ? () => requestRemove(c) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <ListTree className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{c.name}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">{fieldCount(c.id)} fields</p>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(c.updatedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewCategory(c)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(c)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canCreate ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="glass"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => void handleCopyCategory(c)}
                            disabled={copyBusyId === c.id}
                            aria-label="Copy this category"
                          >
                            {copyBusyId === c.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Copy this category</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => requestRemove(c)}
                        aria-label="Delete category"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this category?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
                ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemoveCategory()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default Categories;
